import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { authenticate } from '@/services/api';
import type { User, AuthResponse } from '@/types';
import { useTelegram } from './TelegramContext';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  adminRole: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: Error | null;
  refetch: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isReady, isTelegram, user: telegramUser } = useTelegram();
  const location = useLocation();
  const isMockboarding = location.pathname.startsWith('/mockboarding');
  const isAdminRoute = location.pathname.startsWith('/admin');
  const [isAuthenticating, setIsAuthenticating] = useState(true);

  // Authenticate on mount
  const { data, isLoading, error, refetch } = useQuery<AuthResponse, Error>({
    queryKey: ['auth'],
    queryFn: async () => {
      if (isTelegram) {
        // Use Telegram initData
        return authenticate();
      } else {
        // Use mock user for dev mode
        return authenticate({
          id: String(telegramUser?.id || 'dev_user_123'),
          username: telegramUser?.username,
          first_name: telegramUser?.first_name,
          last_name: telegramUser?.last_name,
        });
      }
    },
    enabled: isReady && !isMockboarding && !isAdminRoute,
    retry: 1,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (isMockboarding || isAdminRoute || data || error) {
      setIsAuthenticating(false);
    }
  }, [data, error, isAdminRoute, isMockboarding]);

  return (
    <AuthContext.Provider
      value={{
        user: data?.user || null,
        isAdmin: data?.isAdmin || false,
        adminRole: data?.adminRole || null,
        isLoading: isMockboarding || isAdminRoute ? false : isLoading || isAuthenticating || !isReady,
        isAuthenticated: !!data?.user,
        error: error || null,
        refetch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
